import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import type { User as Auth0User } from '@auth0/auth0-react';
import { useAuth0 } from '@auth0/auth0-react';
import { captureException } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory, useLocation } from 'react-router-dom';

import { apiClient } from 'api/ApiClient';
import { baseApiClient } from 'api/BaseApiClient';
import { billingClient } from 'api/billing';
import type { Permission, UserKeysResponse } from 'api/types';
import { SplashScreen } from 'components/SplashScreen';
import { QueryKeys } from 'entities/billing/api/common';
import { useGetProfile } from 'entities/user/api/useGetProfile';
import { useGetProperties } from 'entities/user/api/useGetProperties';
import type { User } from 'entities/user/lib/common';
import type { ProfileResponse } from 'entities/user/types';
import { useGetKeys } from 'entities/userKey/api/useGetKeys';
import { CompanyQueryKeys } from 'hooks/company';
import { useImpersonation } from 'hooks/impersonation';
import type { UnauthenticatedErrorCode } from 'shared/lib/auth/common';
import { CookieKeys, cookieService } from 'shared/lib/cookies/cookieService';
import { authDebug } from 'shared/lib/debug/authDebugger';
import { getEnv } from 'shared/lib/env/getEnv';
import { localStorageService, StorageKeys } from 'shared/lib/storage/storageService';
import { urls } from 'shared/urls';

type AuthState = {
  isInitialized: boolean;
  isAuthenticated: boolean;
  wasAuthenticated: boolean;
  user: User | null;
  isGotError401?: boolean;
  authFailed?: boolean;
  authFailedError?: string;
  error?: UnauthenticatedErrorCode;
  isAuthReady?: boolean; // Track if auth token callback is set
};

type AuthContextValue = {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  signUp: () => Promise<void>;
  updateAuth: () => Promise<void>;
  checkAuth: () => boolean;
  permissions: Set<Permission>;
} & AuthState;

type InitialiseAction = {
  type: 'INITIALISE';
  payload: {
    isAuthenticated: boolean;
    wasAuthenticated: boolean;
    user: User | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User;
  };
};

type CheckAuthAction = {
  type: 'UPDATE_AUTH';
  payload: {
    isAuthenticated: boolean | undefined;
    isAuthReady?: boolean;
  };
};
type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
};

type Error401Action = {
  type: 'ERROR_401';
  payload: {
    isAuthenticated: boolean;
    error?: UnauthenticatedErrorCode;
  };
};

type ErrorAuthFailed = {
  type: 'AUTH_FAILED';
  payload: {
    authFailedError: string;
  };
};

type Action =
  | InitialiseAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | Error401Action
  | ErrorAuthFailed
  | CheckAuthAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  wasAuthenticated: false,
  isAuthReady: false,
};

const isUITest = () => {
  return document.cookie.includes('__dangerously-disable-auth-for-testing');
};

const computePermissions = (userProfile: ProfileResponse | null | undefined) => {
  if (!userProfile) {
    return {
      permissions: new Set<Permission>(),
    };
  }

  const developmentPermissions = new Set<Permission>(['can_view_logs', 'can_view_keys', 'can_view_webhooks']);

  const userPermissions = [...userProfile.permissions];

  if (userProfile.permissions.some((permission: Permission) => developmentPermissions.has(permission))) {
    userPermissions.push('can_view_development');
  }

  return {
    permissions: new Set(userPermissions),
  };
};

const reducer = (state: AuthState, action: Action): AuthState => {
  switch (action.type) {
    case 'INITIALISE': {
      const { isAuthenticated, wasAuthenticated, user } = action.payload;

      return {
        ...state,
        isAuthenticated,
        wasAuthenticated,
        isInitialized: true,
        user,
      };
    }
    case 'LOGIN': {
      const { user } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case 'LOGOUT': {
      return {
        ...state,
        ...initialAuthState,
      };
    }
    case 'ERROR_401': {
      return {
        ...state,
        isGotError401: true,
        error: action.payload.error,
        isAuthenticated: action.payload.isAuthenticated,
      };
    }
    case 'AUTH_FAILED': {
      return {
        ...state,
        authFailed: true,
        authFailedError: action.payload.authFailedError,
        isInitialized: true,
      };
    }
    case 'UPDATE_AUTH': {
      return {
        ...state,
        isAuthenticated: !!action.payload.isAuthenticated,
        isAuthReady: action.payload.isAuthReady ?? state.isAuthReady,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const USER_DATA_ERROR = 'Can`t get user data. Please check if the user is approved at least in one company.';

export const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  permissions: new Set(),
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  updateAuth: () => Promise.resolve(),
  checkAuth: () => false,
});

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const queryClient = useQueryClient();
  const { impersonate, stopImpersonation, [StorageKeys.IMPERSONATION_EMAIL]: impersonationEmail } = useImpersonation();
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const history = useHistory();

  const {
    user,
    isLoading,
    isAuthenticated,
    getAccessTokenSilently,
    handleRedirectCallback,
    loginWithRedirect,
    logout,
  } = useAuth0();

  const [profileQueryEnabled, setProfileQueryEnabled] = useState(false);
  const [keysQueryEnabled, setKeysQueryEnabled] = useState(false);
  const [propertiesQueryEnabled, setPropertiesQueryEnabled] = useState(false);

  const profileQuery = useGetProfile({ enabled: profileQueryEnabled });
  const keysQuery = useGetKeys({ refetchOnMount: false, enabled: keysQueryEnabled });
  const propertiesQuery = useGetProperties({ enabled: propertiesQueryEnabled });

  const { permissions } = useMemo(() => {
    return computePermissions(profileQuery.data);
  }, [profileQuery.data]);

  const login = useCallback(async () => {
    if (isUITest()) {
      return;
    }

    authDebug.log('Auth0Context', 'login() called, redirecting to Auth0');
    try {
      const emailParam = params.get('email');
      const errorParam = params.get('custom_error');
      await loginWithRedirect({
        appState: {
          backUrl: pathname + search,
        },
        authorizationParams: {
          ...(emailParam ? { login_hint: emailParam } : {}),
          ...(errorParam ? { custom_error: errorParam } : {}),
          screen_hint: 'login',
        },
      });
    } catch (loginError) {
      authDebug.error('Auth0Context', 'Login error:', loginError);
    }
  }, [loginWithRedirect, pathname, search, params]);

  const signUp = useCallback(async () => {
    try {
      const emailParam = params.get('email');
      const errorParam = params.get('custom_error');
      await loginWithRedirect({
        authorizationParams: {
          redirect_uri: window.location.origin,
          screen_hint: 'signup',
          ...(emailParam ? { login_hint: emailParam } : {}),
          ...(errorParam ? { custom_error: errorParam } : {}),
        },
      });
    } catch (loginError) {
      authDebug.error('Auth0Context', 'Login error:', loginError);
    }
  }, [loginWithRedirect, params]);

  const logoutCallback = useCallback(async () => {
    try {
      await logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
      dispatch({ type: 'LOGOUT' });
    } catch (logoutError) {
      authDebug.error('Auth0Context', 'Logout error:', logoutError);
    }
  }, [logout]);

  const checkAuth = useCallback(() => {
    if (isUITest()) {
      return true;
    }

    dispatch({ type: 'UPDATE_AUTH', payload: { isAuthenticated } });

    return isAuthenticated;
  }, [isAuthenticated]);

  const fetchUserData = useCallback(
    async (user: Auth0User) => {
      // Check for company_id URL parameter first, then fall back to cookie
      const urlCompanyId = params.get('company_id');

      if (urlCompanyId && urlCompanyId !== cookieService.get(CookieKeys.TENANT_COMPANY_ID)) {
        cookieService.set(CookieKeys.TENANT_COMPANY_ID, urlCompanyId);
      }

      // these request do not require tenant company id in the header
      authDebug.log('Auth0Context', 'Fetching tenant companies');
      const tenantCompanies = await queryClient.fetchQuery({
        queryKey: [CompanyQueryKeys.getTenantCompanies],
        queryFn: () => {
          authDebug.log('Auth0Context', 'Executing getTenantCompanies API call');
          return apiClient.getTenantCompanies();
        },
      });
      authDebug.log('Auth0Context', `Tenant companies fetched: ${tenantCompanies?.length} companies`);

      const tenantCompanyId = cookieService.get(CookieKeys.TENANT_COMPANY_ID);
      const companyExists = !!tenantCompanyId && tenantCompanies?.some((company) => company.id === tenantCompanyId);
      const newTenantId = companyExists ? tenantCompanyId : tenantCompanies?.[0]?.id;

      if (!companyExists) {
        cookieService.remove(CookieKeys.TENANT_COMPANY_ID);
      }

      baseApiClient.setTenantCompanyId(newTenantId);

      const shouldLoadKeys = !!user.email_verified;

      const [userProfile, keys, properties] = await Promise.all([
        profileQuery.refetch().then((result) => result.data),
        shouldLoadKeys
          ? keysQuery
              .refetch()
              .then((result) => result.data as UserKeysResponse)
              .catch(() => null)
          : Promise.resolve(null),
        propertiesQuery.refetch().then((result) => result.data),
      ]);

      // once we loaded user manually, we can enable queries to check for invalidation / refetching
      setProfileQueryEnabled(true);
      if (shouldLoadKeys) {
        setKeysQueryEnabled(true);
      }
      setPropertiesQueryEnabled(true);

      const hasProductionKey = Boolean(keys?.prod?.active_keys?.length);
      const hasDevelopmentKey = Boolean(keys?.dev?.active_keys?.length);

      const isCompanyIndividual = userProfile?.company?.company_type === 'individual';
      const isCompanySelfServe = userProfile?.company?.company_type === 'self_serve';

      return {
        user: {
          id: user.sub ?? '',
          avatar: user.picture ?? '',
          email_verified: user.email_verified,
          email: userProfile?.email ?? '',
          name: `${userProfile?.first_name} ${userProfile?.last_name}`,
          company: userProfile?.company,
          is_owner: userProfile?.role.id === 'owner',
          is_approved: userProfile?.is_approved,
          is_target_account: userProfile?.is_target_account,
          isInformationFormComplete: Boolean(userProfile?.first_name && userProfile?.last_name),
          properties,
          profile: userProfile,
          hasProductionKey,
          hasDevelopmentKey,
          isCompanyIndividual,
          isCompanySelfServe,
          hubspot_identification_token: userProfile?.hubspot_identification_token,
        },
      };
    },
    [queryClient, profileQuery, keysQuery, propertiesQuery, params],
  );

  const initializeState = async () => {
    const initialize = async () => {
      try {
        authDebug.group('Auth0Context', 'Initialization');
        authDebug.log('Auth0Context', 'Starting initialization');
        authDebug.log('Auth0Context', 'URL:', window.location.href);
        authDebug.log('Auth0Context', 'Has code param:', new URLSearchParams(window.location.search).has('code'));
        authDebug.log('Auth0Context', 'Has state param:', new URLSearchParams(window.location.search).has('state'));

        await handleRedirectCallback().catch((err) => {
          authDebug.warn('Auth0Context', 'handleRedirectCallback error (caught):', err);
        });

        if (!checkAuth()) {
          authDebug.log('Auth0Context', 'checkAuth returned false, user not authenticated');
          authDebug.groupEnd();
          throw new Error('User is not authenticated');
        }
        authDebug.log('Auth0Context', 'User is authenticated, setting up auth token callback');

        // eslint-disable-next-line no-underscore-dangle
        baseApiClient.setCallbackAuthToken(async () => {
          authDebug.log('Auth0Context', 'Fetching auth token for API call');
          const { id_token } = await getAccessTokenSilently({
            detailedResponse: true,
            cacheMode: 'on',
          });

          return id_token;
        });
        authDebug.log('Auth0Context', 'Auth token callback has been set');

        // Mark auth as ready after token callback is set
        dispatch({ type: 'UPDATE_AUTH', payload: { isAuthenticated, isAuthReady: true } });

        baseApiClient.setCallbackOn401((response?: { code?: UnauthenticatedErrorCode }) => {
          const error = response?.code;

          dispatch({ type: 'ERROR_401', payload: { isAuthenticated, error } });
        });

        try {
          if (!user) {
            throw new Error('Can`t get Auth0 user');
          }

          authDebug.log('Auth0Context', 'Fetching user data');
          const userData = await fetchUserData(user);
          authDebug.log('Auth0Context', 'User data fetched successfully');

          sessionStorage.removeItem('truv_signup_source');

          if (!userData) {
            throw new Error(USER_DATA_ERROR);
          }

          const role = userData.user.profile?.role?.id;
          const isOrdersManager = role === 'orders_manager';

          const hasProductionKey = userData.user.hasProductionKey;
          const hasDevelopmentKey = userData.user.hasDevelopmentKey;
          const isSandboxMode = userData.user.properties?.isSandboxMode;
          let initialSandbox = true;

          if ((hasProductionKey || hasDevelopmentKey) && (isOrdersManager || !isSandboxMode)) {
            initialSandbox = false;
          } else {
            initialSandbox = true;
          }

          const env = getEnv(initialSandbox, userData.user.profile?.env_types);
          baseApiClient.setEnv(env);

          if (params.get('action') === 'impersonate') {
            if (impersonationEmail) {
              stopImpersonation(true);

              return;
            }

            // Use freshly fetched userProfile data to avoid race condition
            const { permissions: freshPermissions } = computePermissions(userData.user.profile);

            if (freshPermissions.has('can_use_impersonation')) {
              const target = params.get('target') ?? '';
              const id = params.get('id') ?? '';

              impersonate({ target, id });

              return;
            }
          }

          // Early subscription flow redirect before initial render
          try {
            const { permissions: earlyPermissions } = computePermissions(userData.user.profile);

            if (earlyPermissions.has('can_edit_billing') && pathname !== urls.subscriptionFlow.url()) {
              const MAX_REDIRECTS = 2;
              const currentCount = Number(localStorageService.get(StorageKeys.SUBSCRIPTION_REDIRECT_COUNT) ?? 0);

              if (currentCount < MAX_REDIRECTS) {
                const billingProfile = await queryClient.fetchQuery({
                  queryKey: [QueryKeys.getBillingProfile],
                  queryFn: () => billingClient.getBillingProfile(),
                  staleTime: 1000 * 30,
                });

                if (billingProfile && billingProfile.can_subscribe !== false) {
                  localStorageService.set(StorageKeys.SUBSCRIPTION_REDIRECT_COUNT, currentCount + 1);
                  history.push(urls.subscriptionFlow.url());
                }
              }
            }
          } catch (err) {
            authDebug.error('Auth0Context', 'Error redirecting to subscription flow:', err);
            captureException(err);
          }

          authDebug.log('Auth0Context', 'Dispatching INITIALISE with authenticated user');
          authDebug.groupEnd();
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated,
              wasAuthenticated: true,
              ...userData,
            },
          });
        } catch (err) {
          dispatch({
            type: 'INITIALISE',
            payload: {
              isAuthenticated,
              user: null,
              wasAuthenticated: isAuthenticated || state.wasAuthenticated,
            },
          });
        }
      } catch (err) {
        authDebug.error('Auth0Context', 'Initialize error:', err);
        authDebug.groupEnd();
        if (params.get('error') === 'access_denied' || params.get('error') === 'unauthorized') {
          dispatch({
            type: 'AUTH_FAILED',
            payload: {
              authFailedError: params.get('error_description') ?? '',
            },
          });

          return;
        }

        dispatch({
          type: 'INITIALISE',
          payload: {
            isAuthenticated: false,
            wasAuthenticated: state.wasAuthenticated,
            user: null,
          },
        });
      }
    };

    if (isUITest()) {
      // Mark auth as ready for UI tests
      dispatch({ type: 'UPDATE_AUTH', payload: { isAuthenticated: true, isAuthReady: true } });

      const userData = await fetchUserData({ sub: '123', email_verified: true });

      if (!userData) {
        throw new Error(USER_DATA_ERROR);
      }

      dispatch({
        type: 'INITIALISE',
        payload: {
          isAuthenticated: true,
          wasAuthenticated: true,
          ...userData,
        },
      });

      return;
    }

    void initialize();
  };

  const updateAuth = async () => {
    if (isUITest()) {
      return;
    }

    if (isAuthenticated) {
      try {
        await getAccessTokenSilently({
          cacheMode: 'off',
        });
      } catch (error) {
        authDebug.error('Auth0Context', 'Failed to get access token silently:', error);
      }
    }

    void initializeState();
  };

  useEffect(() => {
    authDebug.log('Auth0Context', 'Auth0 isLoading:', isLoading);
    if (isLoading) {
      return;
    }

    authDebug.log('Auth0Context', 'Auth0 loaded, calling initializeState');
    void initializeState();
  }, [isLoading]);

  if (!state.isInitialized) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        permissions,
        login,
        logout: logoutCallback,
        signUp,
        updateAuth,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
